import { Box, styled } from '@mui/material';

import theme from '@setup/theme/theme';

export const setSelectStyle = (
  isError: boolean,
  size: string,
  isStartIcon: boolean,
  isClearable: boolean,
  inputVariant: 'outline' | 'text',
  minWidthSelectMenu?: number,
  maxHeightSelectMenu?: number,
  minHeightSelectMenu?: number,
  menuListCustomStyles?: {},
  isTransparentDefaultBackground?: boolean,
) => ({
  option: (provided: any, state: any) => ({
    ...provided,
    fontFamily: theme.typography.fontFamily,
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '19px',
    padding: '8px',
    marginBottom: '2px',
    overflow: 'hidden',

    '&:last-of-type': {
      marginBottom: '0px',
    },
    backgroundColor: state.isSelected ? theme.palette.case.neutral.n100 : theme.palette.case.contrast.white,
    '&:active': {
      backgroundColor: state.isSelected ? theme.palette.case.neutral.n200 : theme.palette.case.contrast.white,
    },
    color: state.isDisabled
      ? theme.palette.case.neutral.n400
      : state.isSelected
        ? theme.palette.case.neutral.n900
        : theme.palette.case.neutral.n700,
    '&:hover': {
      backgroundColor: state.isDisabled
        ? 'none'
        : state.isSelected
          ? theme.palette.case.neutral.n100
          : theme.palette.case.neutral.n75,
      color: state.isDisabled
        ? theme.palette.case.neutral.n400
        : state.isSelected
          ? theme.palette.case.neutral.n900
          : theme.palette.case.neutral.n700,
      cursor: state.isDisabled ? 'default' : 'pointer',
      transition: 'all 0.3s',
    },
  }),
  multiValue: (provided: any) => ({
    ...provided,
    backgroundColor: theme.palette.case.neutral.n100,
    fontSize: '14px',
  }),
  multiValueRemove: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  container: (provided: any) => ({
    ...provided,
    padding: '0px',
  }),
  dropdownIndicator: (provided: any, state: any) => {
    return {
      ...provided,
      cursor: 'pointer',
      display: state.hasValue && isClearable ? 'none' : 'flex',
      transform: state.selectProps.menuIsOpen ? 'rotate(-180deg)' : 'rotate(0)',
      color: state.isDisabled ? theme.palette.case.neutral.n200 : 'initial',
    };
  },
  clearIndicator: (provided: any) => {
    return {
      ...provided,
      cursor: 'pointer',
    };
  },

  input: (provided: any) => ({
    ...provided,
    margin: 0,
  }),
  control: (provided: any, state: any) => {
    const isTextVariant = inputVariant === 'text';
    let backgroundColor = isTransparentDefaultBackground ? 'transparent' : theme.palette.case.neutral.n50;
    let borderColor = theme.palette.case.neutral.n200;
    let color = theme.palette.case.neutral.n700;

    if (state.isDisabled && !isTextVariant) {
      backgroundColor = theme.palette.case.neutral.n75;
      color = theme.palette.case.neutral.n300;
    } else if (isError) {
      borderColor = theme.palette.case.red.r500;
      backgroundColor = isTextVariant
        ? theme.palette.case.red.r100
        : state.isFocused
          ? theme.palette.case.neutral.n0
          : theme.palette.case.neutral.n50;
    } else if (state.isFocused) {
      backgroundColor = isTextVariant ? theme.palette.case.neutral.n100 : theme.palette.case.neutral.n0;
      borderColor = theme.palette.case.aquamarine.a600;
    } else if (state.hasValue) {
      backgroundColor = isTextVariant ? 'transparent' : theme.palette.case.neutral.n0;
    } else if (state.isDisabled && isTextVariant) {
      backgroundColor = 'transparent';
    }

    return {
      ...provided,
      height: isTextVariant ? '32px' : 36,
      cursor: state.isDisabled ? 'not-allowed' : 'text',
      borderRadius: isTextVariant ? '6px' : '4px',
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '20px',
      fontFamily: theme.typography.fontFamily,
      boxShadow: 'none',
      minHeight: 'auto',
      padding: `0 6px 0 6px !important`,
      color: color,
      backgroundColor: backgroundColor,
      border: isTextVariant ? 'none' : `1px solid ${borderColor}`,
      '&:hover': {
        borderColor: state.isDisabled || state.isFocused ? borderColor : theme.palette.case.neutral.n300,
        backgroundColor:
          state.isDisabled || state.isFocused || isTextVariant
            ? backgroundColor
            : theme.palette.case.neutral.n0,
        transition: 'all 0.3s',
      },
      ...(isTextVariant && {
        '&:hover': { backgroundColor: theme.palette.case.neutral.n75 },
        '&:active': { backgroundColor: theme.palette.case.neutral.n75 },
      }),
    };
  },
  indicatorContainer: (provided: any, state: any) => ({
    ...provided,
    transform: state.isOpened ? '' : '',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    display: 'flex',
    flexWrap: 'nowrap',
    paddingLeft: '0px',
    paddingRight: '0px',
  }),
  indicatorSeparator: () => ({}),
  noOptionsMessage: (provided: any) => ({
    ...provided,
    fontSize: theme.typography.small.fontSize,
    fontWeight: theme.typography.small.fontWeight,
    fontFamily: theme.typography.fontFamily,
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: '4px',
    maxHeight: maxHeightSelectMenu ? `${maxHeightSelectMenu}px` : '175px',
    minHeight: minHeightSelectMenu ? `${minHeightSelectMenu}px` : 'none',
    border: `1px solid ${theme.palette.case.neutral.n200}`,
    backgroundColor: theme.palette.case.neutral.n0,
    borderRadius: '4px',
    minWidth: minWidthSelectMenu ? `${minWidthSelectMenu}px` : '130px',
    overflowY: 'auto',
    ...menuListCustomStyles,
    '&::-webkit-scrollbar': {
      width: '4px !important',
      borderRadius: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.case.neutral.n100,
      width: '4px !important',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.case.neutral.n300,
      width: '4px !important',
      height: '40px !important',
    },
  }),

  menu: (provided: any) => ({
    ...provided,
    minWidth: minWidthSelectMenu ? `${minWidthSelectMenu + 4}px` : '130px',
    boxShadow: theme.palette.case.shadow.default,
    border: `0.5px solid ${theme.palette.case.neutral.n200}`,
    borderRadius: '8px',
    zIndex: 30,
  }),
  placeholder: (provided: any, state: any) => {
    const isTextVariant = inputVariant === 'text';
    let color = theme.palette.case.neutral.n500;
    if (state.isDisabled && isTextVariant) {
      color = `${theme.palette.case.neutral.n700}6B`;
    }
    return {
      ...provided,
      margin: '0',
      transition: 'opacity 200ms',
      color: color,
      position: inputVariant === 'text' ? 'default' : 'absolute',
      whiteSpace: 'nowrap',
    };
  },
  singleValue: (provided: any, state: any) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 300ms';
    const padding = 0;

    return {
      ...provided,
      opacity,
      transition,
      padding,
      margin: '0',
    };
  },
});

export const ReactSelectStartIconContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !['label', 'inputVariant'].includes(prop),
})<{ label: boolean; inputVariant: 'outline' | 'text' }>(({ label, inputVariant }) => ({
  position: 'absolute',
  top: label ? '30px' : inputVariant === 'text' ? '8px' : '9px',
  left: inputVariant === 'text' ? 4 : 8,
  svg: {
    width: '16px',
    height: '16px',
    //   TODO fix icon color in create event modal type select
  },
}));
